import {
  globalizationDownload,
  useGetAllFromGlobalizationQuery,
  useGetRightsQuery,
} from "@/react/features/globalization/globalizationApi";
import TableComponent from "@/react/components/table/TableComponent";
import React, { useCallback, useEffect, useState } from "react";
import { internationalColumnDefinition } from "@/react/features/globalization/columnDefinition";
import { international } from "@/react/features/globalization/schema";
import { OpenInTapFAB } from "@/react/features/globalization/OpenInTapFAB";
import { StyledGroupbyIcon } from "@/react/components/styled/StyledIcons";
import {
  displayToastMessage,
  useDismissableNotification,
} from "@/react/notifications";
import { useSelector } from "react-redux";
import { useTable } from "@/react/components/table/useTable";
import { ExpandedRow } from "@/react/features/globalization/components/cell-renders/ExpandedRow";
import { canSeeEditOption } from "@/react/features/globalization/rights";
import { createSearchParams, useNavigate } from "react-router-dom";
import { GLOBALIZATION_INTERNATIONAL_COMPARE } from "@/react/routing/constants";
import styled from "styled-components";
import {
  FilledEyeIcon,
  FilledDownloadIcon,
  BottomDebouncedInput,
} from "@/react/features/globalization/pages/styles";
import { TYPE } from "@8x8/oxygen-toaster";
import { Button } from "@/react/styled";
import TableWrapper from "@/react/features/styled/TableWrapper";
import Tooltip from "@8x8/oxygen-tooltip";
import SearchIcon from "@8x8/oxygen-icon/novo/SearchIcon";
import { OperationsContainer } from "@/react/features/globalization/OperationsContainer";

const columns = Object.freeze(internationalColumnDefinition);
const MAX_ITEMS_SELECT = 10;
const MIN_ITEMS_SELECT = 2;
const SEARCH_SEPARATOR = ",";

const TableTitle = styled.h3`
  padding: 24px;
  margin-bottom: 0;
`;

export function GlobalizationInternationalPage() {
  const navigate = useNavigate();
  const [tableColumns, setTableColumns] = useState(columns);
  const [filteredInternationalList, setFilteredInternationalList] = useState(
    []
  );
  const [searchString, setSearchString] = useState("");
  const isIframe = useSelector((state) => state.application.isIframe);
  const {
    data: internationalList,
    refetch,
    isFetching,
  } = useGetAllFromGlobalizationQuery();
  const { data: fieldList } = useGetRightsQuery();

  useEffect(() => {
    if (canSeeEditOption(fieldList)) {
      setTableColumns(columns);
    } else {
      setTableColumns(columns.filter((column) => column.id !== "options"));
    }
  }, [fieldList]);

  const filterBySearchTerm = useCallback(
    (searchTerm) => {
      const terms = searchTerm
        .toLowerCase()
        .split(SEARCH_SEPARATOR)
        .filter((term) => term.length > 0)
        .map((term) => term.trim());

      return internationalList.filter((item) =>
        terms.some((term) => item.country.toLowerCase().includes(term))
      );
    },
    [internationalList]
  );

  useEffect(() => {
    if (searchString.length > 0) {
      const filteredInternational = filterBySearchTerm(searchString);
      setFilteredInternationalList(filteredInternational);
    } else {
      setFilteredInternationalList(internationalList);
    }
  }, [internationalList, searchString, filterBySearchTerm]);

  const { table, filters, state, mutations } = useTable(
    filteredInternationalList,
    tableColumns,
    international,
    "id",
    {
      getRowCanExpand: () => true,
    }
  );

  const { updateRows, resetData } = mutations;
  const { rowSelection } = state;

  const selectedIds = Object.entries(rowSelection)
    .filter(([, value]) => value)
    .map(([key]) => key);

  const handleOpenComparePageOrView = () => {
    if (selectedIds.length === 0) {
      return;
    }
    navigate({
      pathname: GLOBALIZATION_INTERNATIONAL_COMPARE,
      search: createSearchParams({ id: selectedIds }).toString(),
    });
  };

  const downloadExcel = (ids) => {
    globalizationDownload(
      "international/export?format=xlsx" + (ids ? "&ids=" + ids : "")
    ).then((isDownloaded) => {
      displayToastMessage(
        isDownloaded ? "Download success" : "Failed to download",
        {
          type: isDownloaded ? TYPE.info : TYPE.error,
        }
      );
    });
  };

  const error =
    selectedIds.length > MAX_ITEMS_SELECT
      ? `Maximum of ${MAX_ITEMS_SELECT} elements`
      : false;

  useDismissableNotification(error);

  const isNoSelection = selectedIds.length === 0;
  const isSingleSelection = selectedIds.length === 1;
  const canSelectMultiple =
    selectedIds.length >= MIN_ITEMS_SELECT &&
    selectedIds.length <= MAX_ITEMS_SELECT;

  return (
    <OpenInTapFAB>
      <TableTitle>International Coverage Summary</TableTitle>
      <BottomDebouncedInput
        placeholder="Search by country name"
        value={searchString}
        onChange={(value) => setSearchString(value)}
        prefix={
          <Tooltip title="You can search by multiple countries by separating them with commas">
            <SearchIcon />
          </Tooltip>
        }
      />
      <OperationsContainer isStacked={isIframe}>
        <Button
          variant="success"
          size="small"
          onClick={() => handleOpenComparePageOrView()}
          isDisabled={!(isSingleSelection || canSelectMultiple)}
        >
          {(isSingleSelection || isNoSelection) && (
            <>
              <FilledEyeIcon size={20} /> View
            </>
          )}
          {!isSingleSelection && !isNoSelection && (
            <>
              <StyledGroupbyIcon size={20} /> Compare
            </>
          )}
        </Button>
        <Button
          variant="success"
          size="small"
          onClick={() => downloadExcel(selectedIds)}
          isDisabled={selectedIds.length === 0}
        >
          <FilledDownloadIcon /> Export selected
        </Button>
        <Button variant="success" size="small" onClick={() => downloadExcel()}>
          <FilledDownloadIcon /> Export all
        </Button>
      </OperationsContainer>
      <TableWrapper>
        <TableComponent
          title="International"
          schema={international}
          isLoading={isFetching}
          onCancel={resetData}
          onRefresh={refetch}
          table={table}
          filters={filters}
          mutations={{ updateRows }}
          state={state}
          fileActions={false}
          renderSubComponent={(row) => <ExpandedRow row={row} />}
        />
      </TableWrapper>
    </OpenInTapFAB>
  );
}
